import { useEffect, useRef, useState, useCallback } from 'react';
import { WS_BASE_URL, ECS_BASE_API_URL } from '@/constants/Configs';
import {
  ChatResponseMessage,
  GetCallStatusResponse,
  CallStatus,
} from '@/services/chat.service';

// ============================================================================
// Server -> Client Message Types
// ============================================================================

interface ConnectedEvent {
  type: 'connected';
  chatSessionId: string;
  timestamp: string;
}

interface ChatResponseEvent {
  type: 'chat_response';
  messageId: string;
  chatSessionId: string;
  response: ChatResponseMessage;
  timestamp: string;
}

interface CallStatusEvent {
  type: 'call_status';
  chatSessionId: string;
  status: GetCallStatusResponse;
  timestamp: string;
}

interface AITypingEvent {
  type: 'ai_typing';
  chatSessionId: string;
  isTyping: boolean;
  timestamp: string;
}

interface ErrorEvent {
  type: 'error';
  code: string;
  message: string;
  timestamp: string;
}

interface PongEvent {
  type: 'pong';
  timestamp: string;
}

interface ChatResponseChunkEvent {
  type: 'chat_response_chunk';
  messageId: string;
  chatSessionId: string;
  chunk: string;
  isComplete: boolean;
  timestamp: string;
}

type ServerMessage =
  | ConnectedEvent
  | ChatResponseEvent
  | CallStatusEvent
  | AITypingEvent
  | ErrorEvent
  | PongEvent
  | ChatResponseChunkEvent;

// ============================================================================
// Hook Props & Return Types
// ============================================================================

interface UseChatWebSocketProps {
  chatSessionId: string | null;
  authSessionId: string | null;
  enabled?: boolean;
  onChatResponse?: (response: ChatResponseMessage, messageId: string) => void;
  onChatResponseChunk?: (messageId: string, chunk: string, isComplete: boolean) => void;
  onCallStatus?: (status: GetCallStatusResponse) => void;
  onAITyping?: (isTyping: boolean) => void;
  onConnectionChange?: (connected: boolean) => void;
  onError?: (error: string) => void;
}

interface UseChatWebSocketReturn {
  isConnected: boolean;
  isAITyping: boolean;
  error: string | null;
  sendMessage: (message: string) => boolean;
  disconnect: () => void;
  reconnect: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const MAX_RECONNECT_ATTEMPTS = 5;
const PING_INTERVAL_MS = 30000; // Send ping every 30 seconds

// ============================================================================
// Hook Implementation
// ============================================================================

export const useChatWebSocket = ({
  chatSessionId,
  authSessionId,
  enabled = true,
  onChatResponse,
  onChatResponseChunk,
  onCallStatus,
  onAITyping,
  onConnectionChange,
  onError,
}: UseChatWebSocketProps): UseChatWebSocketReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [isAITyping, setIsAITyping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // WebSocket refs
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectAttempts = useRef(0);
  const chatSessionIdRef = useRef(chatSessionId);
  const shouldReconnectRef = useRef(true);

  // Callback refs to avoid dependency issues
  const callbackRefs = useRef({
    onChatResponse,
    onChatResponseChunk,
    onCallStatus,
    onAITyping,
    onConnectionChange,
    onError,
  });

  useEffect(() => {
    callbackRefs.current = {
      onChatResponse,
      onChatResponseChunk,
      onCallStatus,
      onAITyping,
      onConnectionChange,
      onError,
    };
  }, [onChatResponse, onChatResponseChunk, onCallStatus, onAITyping, onConnectionChange, onError]);

  // Keep chatSessionId ref in sync
  useEffect(() => {
    chatSessionIdRef.current = chatSessionId;
  }, [chatSessionId]);

  /**
   * Send a chat message via WebSocket
   * @returns true if message was sent, false if not connected
   */
  const sendMessage = useCallback((message: string): boolean => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('[ChatWS] Cannot send message - not connected');
      return false;
    }

    if (!chatSessionIdRef.current) {
      console.warn('[ChatWS] Cannot send message - no chat session ID');
      return false;
    }

    try {
      const payload = {
        type: 'chat_message',
        chatSessionId: chatSessionIdRef.current,
        message,
      };
      wsRef.current.send(JSON.stringify(payload));
      console.log('[ChatWS] Message sent via WebSocket');
      return true;
    } catch (sendError) {
      console.error('[ChatWS] Failed to send message:', sendError);
      return false;
    }
  }, []);

  // Convert relative audio URLs to absolute
  const processCallStatus = useCallback((status: GetCallStatusResponse): GetCallStatusResponse => {
    if (status.audioUrl && status.audioUrl.startsWith('/')) {
      return {
        ...status,
        audioUrl: `${ECS_BASE_API_URL}${status.audioUrl}`,
      };
    }
    return status;
  }, []);

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message: ServerMessage = JSON.parse(event.data);

      switch (message.type) {
        case 'connected':
          console.log('[ChatWS] Connected to session:', message.chatSessionId);
          break;

        case 'chat_response':
          console.log('[ChatWS] Received chat response:', message.messageId);
          callbackRefs.current.onChatResponse?.(message.response, message.messageId);
          break;

        case 'chat_response_chunk':
          callbackRefs.current.onChatResponseChunk?.(
            message.messageId,
            message.chunk,
            message.isComplete,
          );
          break;

        case 'call_status':
          console.log('[ChatWS] Received call status:', message.status.status);
          const processedStatus = processCallStatus(message.status);
          callbackRefs.current.onCallStatus?.(processedStatus);
          break;

        case 'ai_typing':
          setIsAITyping(message.isTyping);
          callbackRefs.current.onAITyping?.(message.isTyping);
          break;

        case 'error':
          console.error('[ChatWS] Server error:', message.code, message.message);
          setError(message.message);
          callbackRefs.current.onError?.(message.message);
          // Don't reconnect on auth failures
          if (message.code === 'auth_failed') {
            shouldReconnectRef.current = false;
          }
          break;

        case 'pong':
          // Heartbeat response received
          break;
      }
    } catch (parseError) {
      console.error('[ChatWS] Failed to parse message:', parseError);
    }
  }, [processCallStatus]);

  const startPingInterval = useCallback((ws: WebSocket) => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }

    pingIntervalRef.current = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, PING_INTERVAL_MS);
  }, []);

  const connect = useCallback((sessionId: string, authSession: string) => {
    if (
      wsRef.current?.readyState === WebSocket.CONNECTING ||
      wsRef.current?.readyState === WebSocket.OPEN
    ) {
      return;
    }

    try {
      console.log('[ChatWS] Connecting to chat session:', sessionId);
      const wsUrl = `${WS_BASE_URL}/chat-ws?chatSessionId=${sessionId}&authSessionId=${authSession}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[ChatWS] WebSocket connected');
        setIsConnected(true);
        setError(null);
        reconnectAttempts.current = 0;
        callbackRefs.current.onConnectionChange?.(true);
        startPingInterval(ws);
      };

      ws.onmessage = (event: MessageEvent) => {
        // Ignore messages from stale WebSockets (prevents duplicate messages during reconnect)
        if (wsRef.current !== ws) return;
        handleMessage(event);
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        callbackRefs.current.onConnectionChange?.(false);
        wsRef.current = null;

        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Only reconnect if: should reconnect, abnormal close, not exceeded attempts
        if (
          shouldReconnectRef.current &&
          event.code !== 1000 &&
          reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS
        ) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`[ChatWS] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect(sessionId, authSession);
          }, delay);
        }
      };

      ws.onerror = (event) => {
        console.error('[ChatWS] WebSocket error:', event);
        setError('WebSocket connection error');
        callbackRefs.current.onError?.('WebSocket connection error');
      };
    } catch (connectError) {
      console.error('[ChatWS] Failed to create connection:', connectError);
      setError('Failed to connect');
    }
  }, [handleMessage, startPingInterval]);

  const disconnect = useCallback(() => {
    console.log('[ChatWS] Disconnecting');

    // Prevent any pending reconnection attempts
    shouldReconnectRef.current = false;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }

    setIsConnected(false);
    setError(null);
    reconnectAttempts.current = 0;
  }, []);

  const reconnect = useCallback(() => {
    if (chatSessionId && authSessionId) {
      disconnect();
      setTimeout(() => connect(chatSessionId, authSessionId), 100);
    }
  }, [chatSessionId, authSessionId, disconnect, connect]);

  // Connect/disconnect based on props
  useEffect(() => {
    shouldReconnectRef.current = true;

    if (chatSessionId && authSessionId && enabled) {
      connect(chatSessionId, authSessionId);
    }

    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatSessionId, authSessionId, enabled]);

  return {
    isConnected,
    isAITyping,
    error,
    sendMessage,
    disconnect,
    reconnect,
  };
};
